import { IntlShape } from 'react-intl';

import { UniversalSearchSortOptionEnum } from '@components/UniversalSearch';
import {
  AutomationActionBlockKindEnum,
  AutomationConditionKindEnum,
  AutomationTriggerBlockFrequencyEnum,
  AutomationTriggerBlockKindEnum,
  AutomationUserTypeEnum,
  BillableCalculationEnum,
  CareProfileActionableFieldEnum,
  CareProfileAgeEnum,
  CareProfileCareNeedEnum,
  CareProfileConcernEnum,
  CareProfileEmergencyPlanItemEnum,
  CareProfileEmotionalConcernEnum,
  CareProfileFallCountEnum,
  CareProfileGuidanceTypeEnum,
  CareProfileMemberNeedsEnum,
  CareProfileProviderFinancialConcernEnum,
  CareProfileResidenceTypeEnum,
  CareProfileSocialSatisfactionEnum,
  CareProfileStabilitySupportEnum,
  CareProfileStageLimitedEnum,
  CareProfileUrgencyEnum,
  CareProfileVeteranConnectionEnum,
  ClientRoleRelationshipEnum,
  ConsultDurationEnum,
  ConsultTopicEnum,
  ContactMethodEnum,
  DurationUnitsEnum,
  FocusAreaEnum,
  JourneysSearchDateRangeTypeEnum,
  MedicationFormatEnum,
  MedicationKindEnum,
  MedicationPaymentTypeEnum,
  PushNotificationActionEnum,
  SignedDocumentKindEnum,
  StageEnum,
  TaskClientVisibleEnum,
  TaskKindEnum,
  TrackerTypeEnum
} from '@typing/Generated';
import {
  getCareProfileActionableFieldCategory,
  getCareProfileAnswerOptionsForActionableField
} from '@utils/careProfileUtils';
import { ImageName } from '@utils/imageUtils';

export const optionsForAge = (intl: IntlShape, skipDeceased: boolean = false) => {
  let options = [
    CareProfileAgeEnum.sixty_five_and_older,
    CareProfileAgeEnum.eighteen_to_sixty_four,
    CareProfileAgeEnum.under_eighteen,
    CareProfileAgeEnum.deceased
  ];
  if (skipDeceased) {
    options = options.filter(option => option !== CareProfileAgeEnum.deceased);
  }
  return options.map(option => ({
    label: intl.formatMessage({ id: `models.careProfile.age.${option}` }),
    value: option
  }));
};

export const optionsForBillableCalculation = (intl: IntlShape) =>
  Object.values(BillableCalculationEnum).map(option => ({
    label: intl.formatMessage({ id: `models.employerContract.billableCalculation.${option}` }),
    value: option
  }));

export const optionsForBoolean = (intl: IntlShape) => [
  { label: intl.formatMessage({ id: 'dictionary.yes' }), value: true },
  { label: intl.formatMessage({ id: 'dictionary.no' }), value: false }
];

export const optionsForConcerns = (intl: IntlShape) => {
  const options = [
    CareProfileConcernEnum.functional,
    CareProfileConcernEnum.medical,
    CareProfileConcernEnum.social,
    CareProfileConcernEnum.legal,
    CareProfileConcernEnum.financial
  ];
  return options.map(option => ({
    help: intl.formatMessage({ id: `models.careProfile.questions.concerns.options.${option}.help` }),
    label: intl.formatMessage({ id: `models.careProfile.questions.concerns.options.${option}.content` }),
    value: option
  }));
};

export const optionsForEmotionalConcern = (intl: IntlShape) => {
  const options = [
    CareProfileEmotionalConcernEnum.depression,
    CareProfileEmotionalConcernEnum.anxiety,
    CareProfileEmotionalConcernEnum.isolation,
    CareProfileEmotionalConcernEnum.weight_changes,
    CareProfileEmotionalConcernEnum.sleep_problems,
    CareProfileEmotionalConcernEnum.none,
    CareProfileEmotionalConcernEnum.other
  ];
  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.careProfile.questions.emotional_concerns.options.${option}.content`
    }),
    value: option
  }));
};

export const optionsForFallCount = (intl: IntlShape) => {
  const options = [CareProfileFallCountEnum.two_or_fewer, CareProfileFallCountEnum.three_or_more];

  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.careProfile.questions.fall_count.options.${option}.content`
    }),
    value: option
  }));
};

export const optionsForFinancialConcern = (intl: IntlShape) => {
  const options = [
    {
      help: intl.formatMessage({
        id: 'models.careProfile.questions.provider_financial_concerns.options.planning_for_future.help'
      }),
      option: CareProfileProviderFinancialConcernEnum.planning_for_future
    },
    {
      help: intl.formatMessage({
        id: 'models.careProfile.questions.provider_financial_concerns.options.qualifying_for_financial_programs.help'
      }),
      option: CareProfileProviderFinancialConcernEnum.qualifying_for_financial_programs
    },
    {
      help: intl.formatMessage({
        id: 'models.careProfile.questions.provider_financial_concerns.options.paying_for_care.help'
      }),
      option: CareProfileProviderFinancialConcernEnum.paying_for_care
    },

    {
      help: intl.formatMessage({
        id: 'models.careProfile.questions.provider_financial_concerns.options.managing_finances.help'
      }),
      option: CareProfileProviderFinancialConcernEnum.managing_finances
    },
    { option: CareProfileProviderFinancialConcernEnum.no },
    { option: CareProfileProviderFinancialConcernEnum.other }
  ];
  return options.map(option => ({
    help: option.help,
    label: intl.formatMessage({
      id: `models.careProfile.questions.provider_financial_concerns.options.${option.option}.content`
    }),
    value: option.option
  }));
};

export const optionsForFocusArea = (intl: IntlShape, allOption = true) => {
  const options = Object.values(FocusAreaEnum).map(option => ({
    label: intl.formatMessage({ id: `models.focusArea.${option}` }),
    value: option as string
  }));
  if (allOption) {
    options.unshift({ label: intl.formatMessage({ id: 'dictionary.all' }), value: '' });
  }
  return options;
};

export const optionsForGuidanceType = (intl: IntlShape, includeImages: boolean = false) => {
  const options = [
    { image: 'femaleDoctorAtComputer', value: CareProfileGuidanceTypeEnum.EXPERT_GUIDED },
    { image: 'manSittingOnBookPile', value: CareProfileGuidanceTypeEnum.SELF_GUIDED }
  ];

  return options.map(option => ({
    image: includeImages ? (option.image as ImageName) : undefined,
    label: intl.formatMessage({ id: `models.careProfile.guidanceType.${option.value}` }),
    value: option.value
  }));
};

export const optionsForStabilitySupport = (intl: IntlShape) => {
  const options = [
    CareProfileStabilitySupportEnum.cane_or_walker,
    CareProfileStabilitySupportEnum.wheelchair,
    CareProfileStabilitySupportEnum.objects,
    CareProfileStabilitySupportEnum.none
  ];

  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.careProfile.questions.stability_supports.options.${option}.content`
    }),
    value: option
  }));
};

export const optionsForResidenceType = (intl: IntlShape) => {
  const options = [
    { option: CareProfileResidenceTypeEnum.home_alone },
    { option: CareProfileResidenceTypeEnum.home_with_others },
    { option: CareProfileResidenceTypeEnum.independent_living_community },
    {
      help: intl.formatMessage({
        id: 'models.careProfile.questions.residence_type.options.assisted_living_community.help'
      }),
      option: CareProfileResidenceTypeEnum.assisted_living_community
    },
    {
      help: intl.formatMessage({
        id: 'models.careProfile.questions.residence_type.options.memory_care_community.help'
      }),
      option: CareProfileResidenceTypeEnum.memory_care_community
    },
    {
      help: intl.formatMessage({
        id: 'models.careProfile.questions.residence_type.options.skilled_nursing_facility.help'
      }),
      option: CareProfileResidenceTypeEnum.skilled_nursing_facility
    }
  ];
  return options.map(option => ({
    help: option.help,
    label: intl.formatMessage({
      id: `models.careProfile.questions.residence_type.options.${option.option}.content`
    }),
    value: option.option
  }));
};

export const optionsForSocialSatisfaction = (intl: IntlShape) => {
  const options = [
    { icon: 1, option: CareProfileSocialSatisfactionEnum.not_at_all },
    { icon: 2, option: CareProfileSocialSatisfactionEnum.level_2 },
    { icon: 3, option: CareProfileSocialSatisfactionEnum.neutral },
    { icon: 4, option: CareProfileSocialSatisfactionEnum.level_4 },
    { icon: 5, option: CareProfileSocialSatisfactionEnum.extremely }
  ];

  return options.map(option => ({
    iconName: option.icon,
    label: intl.formatMessage({
      id: `models.careProfile.questions.social_satisfaction.options.${option.option}.content`
    }),
    value: option.option
  }));
};

export const optionsForStage = (intl: IntlShape) => {
  const options = [
    StageEnum.independent,
    StageEnum.interdependent,
    StageEnum.dependent,
    StageEnum.crisis,
    StageEnum.end_of_life,
    StageEnum.bereavement
  ];
  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.journey.stage.${option}`
    }),
    value: option
  }));
};

export const optionsForStageLimited = (intl: IntlShape) => {
  const options = [
    CareProfileStageLimitedEnum.independent,
    CareProfileStageLimitedEnum.interdependent,
    CareProfileStageLimitedEnum.dependent
  ];
  return options.map(option => ({
    label: intl.formatMessage({ id: `models.careProfile.stageLimited.${option}` }),
    value: option
  }));
};

export const optionsForVeteranConnection = (intl: IntlShape) => {
  const options = [
    CareProfileVeteranConnectionEnum.self,
    CareProfileVeteranConnectionEnum.spouse,
    CareProfileVeteranConnectionEnum.neither
  ];
  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.careProfile.questions.veteran_connection.options.${option}.content`
    }),
    value: option
  }));
};

export const optionsForEmergencyPlanItems = (intl: IntlShape) => {
  const options = [
    CareProfileEmergencyPlanItemEnum.response_button,
    CareProfileEmergencyPlanItemEnum.cell_phone,
    CareProfileEmergencyPlanItemEnum.able_to_call_911,
    CareProfileEmergencyPlanItemEnum.able_to_exit_home,
    CareProfileEmergencyPlanItemEnum.none
  ];

  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.careProfile.questions.emergency_plan_items.options.${option}.content`
    }),
    value: option
  }));
};

const translateOptions = (options: string[], intl: IntlShape, prefix: string, suffix?: string) =>
  options.map(option => ({
    label: intl.formatMessage({
      id: `${prefix}.${option}${suffix ? `.${suffix}` : ''}`
    }),
    value: option
  }));

export type ConsultTopicCategory = 'guide' | 'research' | 'saveTime';

export const consultTopicOrder: { [key in ConsultTopicCategory]: string[] } = {
  guide: [
    ConsultTopicEnum.HANDLE_REFERRALS,
    ConsultTopicEnum.MEDIATE_FAMILY_MEETING,
    ConsultTopicEnum.ARRANGE_AUTISM_EVALUATION,
    ConsultTopicEnum.RELOCATION_GUIDANCE,
    ConsultTopicEnum.ASSISTED_LIVING_SUPPORT,
    ConsultTopicEnum.ARRANGE_MEDICAL_SECOND_OPINION,
    ConsultTopicEnum.ARRANGE_MEMORY_LOSS_EVALUATION,
    ConsultTopicEnum.COMPARE_CARE_COSTS
  ],
  research: [
    ConsultTopicEnum.HOUSING_OPTIONS,
    ConsultTopicEnum.FIND_LAWYER,
    ConsultTopicEnum.FIND_DOCTOR,
    ConsultTopicEnum.FIND_RESPITE_OPTIONS,
    ConsultTopicEnum.FIND_SUPPORT_GROUPS,
    ConsultTopicEnum.FIND_IN_HOME_AIDE,
    ConsultTopicEnum.FIND_FINANCIAL_PLANNER,
    ConsultTopicEnum.MENTAL_HEALTH_RESOURCES
  ],
  saveTime: [
    ConsultTopicEnum.SCHEDULE_APPOINTMENT,
    ConsultTopicEnum.INTERVIEW_POTENTIAL_CAREGIVERS,
    ConsultTopicEnum.ARRANGE_RIDE,
    ConsultTopicEnum.SUBMIT_INSURANCE_CLAIMS,
    ConsultTopicEnum.ARRANGE_HOME_SAFETY_EVALUATION,
    ConsultTopicEnum.COMPLETE_APPLICATIONS,
    ConsultTopicEnum.COORDINATE_EMERGENCY_RESPONSE
  ]
};

export const optionsForRelationship = (
  intl: IntlShape,
  translationPrefix: string,
  skipOther: boolean = false,
  includeImages: boolean = false
) => {
  let options = [
    { image: 'huggingHeart', value: ClientRoleRelationshipEnum.myself },
    { image: 'givingFlowers', value: ClientRoleRelationshipEnum.spouse },
    { image: 'manSittingWithLaptop', value: ClientRoleRelationshipEnum.parent },
    { image: 'family', value: ClientRoleRelationshipEnum.parent_in_law },
    { image: 'dancingSeniors', value: ClientRoleRelationshipEnum.grandchild },
    { image: 'manWithSenior', value: ClientRoleRelationshipEnum.grandparent },
    { image: 'happyFamily', value: ClientRoleRelationshipEnum.child },
    { image: 'happyWomenJumping', value: ClientRoleRelationshipEnum.sibling },
    { image: 'manHoldingLargePhone', value: ClientRoleRelationshipEnum.relative },
    { image: 'fishingInBoat', value: ClientRoleRelationshipEnum.friend },
    { image: 'womanChairComputer', value: ClientRoleRelationshipEnum.other }
  ];

  if (skipOther) {
    options = options.filter(option => option.value !== ClientRoleRelationshipEnum.other);
  }
  return options.map(option => ({
    image: includeImages ? (option.image as ImageName) : undefined,
    label: intl.formatMessage({ id: `${translationPrefix}.${option.value}` }),
    value: option.value
  }));
};

export const optionsForUrgency = (intl: IntlShape, includeImages: boolean = false) => {
  const options = [
    { image: 'womanAlarmClock', value: CareProfileUrgencyEnum.asap },
    { image: 'womanSittingOnGiantMugNoSteam', value: CareProfileUrgencyEnum.soon },
    { image: 'manWithGiantMagnifyingGlass', value: CareProfileUrgencyEnum.just_browsing }
  ];

  return options.map(option => ({
    image: includeImages ? (option.image as ImageName) : undefined,
    label: intl.formatMessage({ id: `models.careProfile.urgency.${option.value}` }),
    value: option.value
  }));
};

export const optionsForMemberNeeds = (intl: IntlShape) => {
  const options = [
    CareProfileMemberNeedsEnum.medical_care,
    CareProfileMemberNeedsEnum.non_medical_care,
    CareProfileMemberNeedsEnum.mental_health_care,
    CareProfileMemberNeedsEnum.housing,
    CareProfileMemberNeedsEnum.education,
    CareProfileMemberNeedsEnum.legal_needs,
    CareProfileMemberNeedsEnum.finances,
    CareProfileMemberNeedsEnum.future_planning
  ];

  return options.map(option => ({
    label: intl.formatMessage({ id: `models.careProfile.memberNeeds.${option}` }),
    value: option
  }));
};

export const optionsForConsultTopics = (category: ConsultTopicCategory, intl: IntlShape) =>
  translateOptions(consultTopicOrder[category], intl, 'models.consult.topics.options');

export const optionsForAutomationActionBlockKinds = (intl: IntlShape) => {
  const options = [
    AutomationActionBlockKindEnum.ADD_TAG,
    AutomationActionBlockKindEnum.APPLY_CARE_PLAN_TASK_TEMPLATE,
    AutomationActionBlockKindEnum.APPLY_GOAL_TEMPLATE,
    AutomationActionBlockKindEnum.ARCHIVE_TAG,
    AutomationActionBlockKindEnum.COMPLETE_ALL_TASKS_FROM_TEMPLATE,
    AutomationActionBlockKindEnum.COMPLETE_ALL_TASKS_OF_KIND,
    AutomationActionBlockKindEnum.CREATE_MESSAGE,
    AutomationActionBlockKindEnum.CREATE_PUSH_NOTIFICATION,
    AutomationActionBlockKindEnum.CREATE_TASK,
    AutomationActionBlockKindEnum.NO_OP,
    AutomationActionBlockKindEnum.RECOMMEND_AN_ARTICLE,
    AutomationActionBlockKindEnum.REPLACE_TAG,
    AutomationActionBlockKindEnum.RECOMMEND_A_WORKSHEET_TEMPLATE
  ];

  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.automationActionBlock.kindOptions.${option}`
    }),
    value: option
  }));
};

export const optionsForTaskKinds = (intl: IntlShape) => {
  const options = Object.values(TaskKindEnum);

  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.tasks.kind.${option}`
    }),
    value: option
  }));
};

export const optionsForAutomationTrigge = (intl: IntlShape) => {
  const options = [DurationUnitsEnum.MINUTES, DurationUnitsEnum.HOURS, DurationUnitsEnum.DAYS];

  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.automationTimingBlock.durationOptions.${option}`
    }),
    value: option
  }));
};

export const optionsForDurationUnits = (intl: IntlShape, defaultOptions?: DurationUnitsEnum[]) => {
  const options = defaultOptions ?? [DurationUnitsEnum.MINUTES, DurationUnitsEnum.HOURS, DurationUnitsEnum.DAYS];

  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.automationTimingBlock.durationOptions.${option}`
    }),
    value: option
  }));
};

export const optionsForHour = (intl: IntlShape) => {
  const options = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.hour.hourOptions.${option}`
    }),
    value: option
  }));
};

export const optionsForPushNotificationAction = (intl: IntlShape) => {
  const options = [
    PushNotificationActionEnum.OPEN_CARE_PLAN,
    PushNotificationActionEnum.OPEN_CONSULTS,
    PushNotificationActionEnum.OPEN_MESSAGES
  ];

  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.pushNotificationActions.${option}`
    }),
    value: option
  }));
};

export const optionsForSignedDocumentKind = (intl: IntlShape) => {
  const options = [SignedDocumentKindEnum.CONSENT_FORM];

  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.signedDocument.kindOptions.${option}`
    }),
    value: option
  }));
};

export const optionsForTaskClientVisible = (intl: IntlShape) => {
  const options = [
    TaskClientVisibleEnum.ANY_TASK,
    TaskClientVisibleEnum.CLIENT_VISIBLE,
    TaskClientVisibleEnum.EXPERT_ONLY
  ];

  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.tasks.taskClientVisibleOptions.${option}`
    }),
    value: option
  }));
};

export const optionsForTrackerType = (intl: IntlShape) => {
  const options = [
    TrackerTypeEnum.legal_documents,
    TrackerTypeEnum.medication_tracker,
    TrackerTypeEnum.personalize_my_experience,
    TrackerTypeEnum.provider_tracker
  ];

  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.tracker.typeOptions.${option}`
    }),
    value: option
  }));
};

export const optionsForAutomationTriggerBlockKinds = (intl: IntlShape) => {
  const options = [
    AutomationTriggerBlockKindEnum.ARTICLE_OPENED,
    AutomationTriggerBlockKindEnum.CARE_PLAN_ACTION_ITEM_COMPLETED,
    AutomationTriggerBlockKindEnum.CARE_PLAN_PUBLISHED,
    AutomationTriggerBlockKindEnum.CARE_PROFILE_UPDATED,
    AutomationTriggerBlockKindEnum.CARE_TEAM_INVITE_ACCEPTED,
    AutomationTriggerBlockKindEnum.CARE_TEAM_INVITE_SENT,
    AutomationTriggerBlockKindEnum.CLIENT_LOCATION_CHANGED,
    AutomationTriggerBlockKindEnum.CLIENT_RELATIONSHIP_CHANGED,
    AutomationTriggerBlockKindEnum.CONSULT_CANCELED,
    AutomationTriggerBlockKindEnum.CONSULT_SCHEDULED,
    AutomationTriggerBlockKindEnum.EMPLOYER_BENEFIT_OPENED,
    AutomationTriggerBlockKindEnum.FILE_RESOURCE_ADDED,
    AutomationTriggerBlockKindEnum.FREE_TEXT_KEYWORD_MATCHED,
    AutomationTriggerBlockKindEnum.GOAL_ADDED,
    AutomationTriggerBlockKindEnum.INITIAL_EXPERT_ASSIGNED,
    AutomationTriggerBlockKindEnum.JOURNEY_ADDED,
    AutomationTriggerBlockKindEnum.JOURNEY_TAG_ADDED,
    AutomationTriggerBlockKindEnum.LOSES_EXPERT_ACCESS_UPCOMING,
    AutomationTriggerBlockKindEnum.MESSAGE_SENT,
    AutomationTriggerBlockKindEnum.NEVER,
    AutomationTriggerBlockKindEnum.PACKAGE_GRANT_PURCHASED,
    AutomationTriggerBlockKindEnum.PACKAGE_GRANT_EXPIRATION_UPCOMING,
    AutomationTriggerBlockKindEnum.PACKAGE_GRANT_ADDED,
    AutomationTriggerBlockKindEnum.RECOMMENDED_NEXT_CONSULT_UPCOMING,
    AutomationTriggerBlockKindEnum.RESOURCE_ADDED,
    AutomationTriggerBlockKindEnum.RESOURCE_MARKED_NOT_USEFUL,
    AutomationTriggerBlockKindEnum.RESOURCE_MARKED_USEFUL,
    AutomationTriggerBlockKindEnum.RESOURCE_PINNED,
    AutomationTriggerBlockKindEnum.RESOURCE_UNPINNED,
    AutomationTriggerBlockKindEnum.SIGNED_DOCUMENT_COMPLETED,
    AutomationTriggerBlockKindEnum.TASK_ADDED,
    AutomationTriggerBlockKindEnum.TASK_COMPLETED,
    AutomationTriggerBlockKindEnum.TIME_LOG_ADDED,
    AutomationTriggerBlockKindEnum.TIME_SINCE_LAST_CONSULT,
    AutomationTriggerBlockKindEnum.TRACKER_COMPLETED,
    AutomationTriggerBlockKindEnum.TRACKER_STARTED,
    AutomationTriggerBlockKindEnum.WORKSHEET_COMPLETED,
    AutomationTriggerBlockKindEnum.WORKSHEET_INCOMPLETED,
    AutomationTriggerBlockKindEnum.WORKSHEET_STARTED
  ];

  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.automationTriggerBlock.kindOptions.${option}`
    }),
    value: option
  }));
};

export const optionsForAutomationUserType = (
  intl: IntlShape,
  availableTypes: AutomationUserTypeEnum[] = Object.values(AutomationUserTypeEnum)
) => {
  const options = [
    AutomationUserTypeEnum.ANYONE,
    AutomationUserTypeEnum.CLIENT_ANY,
    AutomationUserTypeEnum.CLIENT_OWNER,
    AutomationUserTypeEnum.EXPERT_ANY,
    AutomationUserTypeEnum.EXPERT_PRIMARY
  ];

  return options
    .filter(o => availableTypes.includes(o))
    .map(option => ({
      label: intl.formatMessage({
        id: `models.automationTriggerBlock.userTypeOptions.${option}`
      }),
      value: option
    }));
};

export const optionsForCareNeeds = (intl: IntlShape) => {
  const options = [
    CareProfileCareNeedEnum.SCHEDULING_APPOINTMENTS,
    CareProfileCareNeedEnum.INSURANCE_DENIALS,
    CareProfileCareNeedEnum.SECOND_OPINIONS,
    CareProfileCareNeedEnum.TRANSPORTATION,
    CareProfileCareNeedEnum.MEAL_DELIVERY,
    CareProfileCareNeedEnum.EMERGENCY_RESPONSE_SYSTEMS,
    CareProfileCareNeedEnum.IN_HOME_AID,
    CareProfileCareNeedEnum.CRISIS_SUPPORT,
    CareProfileCareNeedEnum.AFFORDABLE_THERAPIST,
    CareProfileCareNeedEnum.PSYCHIATRIC_HOSPITALIZATION,
    CareProfileCareNeedEnum.HOUSING_OPTIONS,
    CareProfileCareNeedEnum.RELOCATION,
    CareProfileCareNeedEnum.ASSISTED_LIVING,
    CareProfileCareNeedEnum.AFTER_SCHOOL_CARE,
    CareProfileCareNeedEnum.EDUCATION_ACCOMODATIONS,
    CareProfileCareNeedEnum.DEVELOPMENTAL_EVALUATION,
    CareProfileCareNeedEnum.LEGAL_DOCUMENTS,
    CareProfileCareNeedEnum.FIND_A_LAWYER,
    CareProfileCareNeedEnum.IMMIGRATION_ASSISTANCE
  ];

  return options.map(option => ({
    label: intl.formatMessage({
      id: `models.careProfile.questions.careNeeds.options.${option}.content`
    }),
    value: option
  }));
};

export const optionsForAutomationTriggerBlockFrequency = (
  intl: IntlShape,
  availableFrequencies: AutomationTriggerBlockFrequencyEnum[]
) => {
  const options = [AutomationTriggerBlockFrequencyEnum.EVERY_TIME, AutomationTriggerBlockFrequencyEnum.FIRST_TIME_ONLY];

  return options
    .filter(o => availableFrequencies.includes(o))
    .map(option => ({
      label: intl.formatMessage({
        id: `models.automationTriggerBlock.frequencyOptions.${option}`
      }),
      value: option
    }));
};

export const optionsForCareProfileActionableField = (intl: IntlShape) =>
  Object.values(CareProfileActionableFieldEnum).map(fieldOption => ({
    label: intl.formatMessage({
      id: `models.${getCareProfileActionableFieldCategory(fieldOption)}.questions.${fieldOption}.content`
    }),
    value: fieldOption
  }));

export const optionsForCareProfileActionableFieldValue = (
  careProfileField: CareProfileActionableFieldEnum | null,
  intl: IntlShape
) => {
  if (careProfileField) {
    const options = getCareProfileAnswerOptionsForActionableField(careProfileField);

    if (careProfileField) {
      return options.map(option => ({
        label: intl.formatMessage({
          id: `models.${getCareProfileActionableFieldCategory(
            careProfileField
          )}.questions.${careProfileField}.options.${option}.content`
        }),
        value: option
      }));
    }
  }
  return [];
};

export const optionsForAutomationConditionKind = (intl: IntlShape) => {
  const options = [
    AutomationConditionKindEnum.CARE_PLAN_IS_PUBLISHED,
    AutomationConditionKindEnum.CARE_PROFILE_VALUE_IS,
    AutomationConditionKindEnum.FUTURE_CONSULT_SCHEDULED,
    AutomationConditionKindEnum.OWNER_EMPLOYER_IS,
    AutomationConditionKindEnum.MEMBER_MESSAGE_SINCE,
    AutomationConditionKindEnum.NEVER,
    AutomationConditionKindEnum.TIME_SINCE_LAST_CONSULT
  ];
  return options.map(kindOption => ({
    label: intl.formatMessage({ id: `models.automationCondition.kinds.${kindOption}` }),
    value: kindOption
  }));
};

export const optionsForJourneySearchDateRangeType = (intl: IntlShape) => {
  const options = [JourneysSearchDateRangeTypeEnum.CREATED_AT, JourneysSearchDateRangeTypeEnum.LAST_ACTIVITY_AT];
  return options.map(kindOption => ({
    label: intl.formatMessage({ id: `models.journeysSearch.dateRangeTypes.${kindOption}` }),
    value: kindOption
  }));
};

export const optionsForJourneySearchSortBy = (intl: IntlShape) => {
  const options = [
    UniversalSearchSortOptionEnum.SCORE,
    UniversalSearchSortOptionEnum.LAST_ACTIVITY_AT_DESC,
    UniversalSearchSortOptionEnum.LAST_ACTIVITY_AT_ASC,
    UniversalSearchSortOptionEnum.CREATED_AT_DESC,
    UniversalSearchSortOptionEnum.CREATED_AT_ASC
  ];
  return options.map(kindOption => ({
    label: intl.formatMessage({ id: `models.journeysSearch.sortBy.${kindOption}` }),
    value: kindOption
  }));
};

export const optionsForMedicationKind = (intl: IntlShape) => [
  {
    label: intl.formatMessage({ id: 'models.client.medication.prescription' }),
    value: MedicationKindEnum.prescription
  },
  {
    label: intl.formatMessage({ id: 'models.client.medication.overTheCounter' }),
    value: MedicationKindEnum.over_the_counter
  },
  {
    label: intl.formatMessage({ id: 'models.client.medication.vitaminOrSupplement' }),
    value: MedicationKindEnum.vitamin_or_supplement
  }
];

export const optionsForMedicationFormat = (intl: IntlShape) => {
  const options = [
    MedicationFormatEnum.injection,
    MedicationFormatEnum.oral,
    MedicationFormatEnum.suppository,
    MedicationFormatEnum.topical,
    MedicationFormatEnum.other
  ];
  return options.map(option => ({
    label: intl.formatMessage({ id: `models.client.medication.${option}` }),
    value: option
  }));
};

export const optionsForMedicationPaymentType = (intl: IntlShape) => {
  const options = [
    MedicationPaymentTypeEnum.covered_by_insurance,
    MedicationPaymentTypeEnum.self_pay,
    MedicationPaymentTypeEnum.unknown
  ];
  return options.map(option => ({
    label: intl.formatMessage({ id: `models.client.medication.${option}` }),
    value: option
  }));
};

export const lightenColor = (color: string, percent: number) => {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const B = ((num >> 8) & 0x00ff) + amt;
  const G = (num & 0x0000ff) + amt;
  return (
    '#' +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (B < 255 ? (B < 1 ? 0 : B) : 255) * 0x100 +
      (G < 255 ? (G < 1 ? 0 : G) : 255)
    )
      .toString(16)
      .slice(1)
  );
};

export const durationOptions = (intl: IntlShape) =>
  [ConsultDurationEnum.thirty_minutes, ConsultDurationEnum.one_hour].map(option => ({
    label: intl.formatMessage({ id: `models.duration.${option}` }),
    value: option
  }));

export const contactMethodOptions = (intl: IntlShape) =>
  [ContactMethodEnum.ZOOM, ContactMethodEnum.PHONE].map(option => ({
    label: intl.formatMessage({ id: `pages.consults.contactMethod.${option}` }),
    value: option
  }));
